import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-bootstrap'
import CalcSection from './CalcSection'
import PopOver from './PopOver'
import CalcParagraph from './CalcParagraph'
import FormattedConnectedInput from '../forms/FormattedConnectedInput'
import FormNumberTotal from '../forms/FormNumberTotal'
import * as sectionConstants from './section'
import * as formConstants from '../forms/form'
import NavBtn from './NavBtn'
import NavBar from './NavBar'
import ALink from './ALink'
import HelpFilter from './HelpFilter'

/**
 * Basic component for rendering Section 1 of the calculator
 */
export default class Section1 extends Component {
  /**
   *
   */
  render () {
    return (
      <CalcSection
        sectionProgress={this.props.sectionProgress}
        title={this.props.title}
      >
        <CalcParagraph>
          The first question to ask when planning for retirement is, "How much
          will I need to spend per month in retirement?" There are several other
          approaches to retirement planning but this question focuses your
          effort on a very tangible and realistic set of expectations and avoids
          the often self-defeating modern mantra of "More is better." There is
          nothing inherently wrong with wanting "more" or acquiring wealth but
          the fact is that many people can and will be satisfied with a more
          realistic and grounded understanding of what they need. As important
          as this idea is, expanding on this topic is beyond what we're trying
          to do here. If you are interested in learning more about the power of
          the messages that nature and nurture have given us about acquiring
          wealth versus what a vast majority of people need to live a fulfilling
          life, the book
          <ALink href='http://vickirobin.com/books/summary-of-your-money-or-your-life/'>
            Your Money Or Your Life
          </ALink>
          is a good place to start.
        </CalcParagraph>
        <CalcParagraph>
          To determine what your monthly costs will be in retirement is, let’s
          start with "The Basics":
        </CalcParagraph>
        <ul style={sectionConstants.listStyle}>
          <li>Housing</li>
          <li>Utilities</li>
          <li>Food</li>
          <li>Transportation</li>
          <li>Insurance</li>
        </ul>
        <CalcParagraph>
          This covers the essentials. Having these things covered for the rest
          of your life would free you up to work on some pretty interesting
          stuff! This is where the first work comes in: you need to determine
          what each of these is going to cost you per month in retirement.
        </CalcParagraph>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            First, think about how much you will ideally pay for housing in
            retirement per month.
          </HelpFilter>{' '}
          <HelpFilter showLevel={[1]}>
            If you can’t think of a number right off the bat, just think about
            how much you pay for your current place.
          </HelpFilter>{' '}
          Don’t worry about inflation or anything like that, we’ll take care of
          that later.
          <HelpFilter showLevel={[1, 2]}>
            Just think about where you want to live and what kind of place you
            want to live in and provide your best guess at the monthly expense.
            Still not sure?{' '}
            <PopOver linkText='Consider using the U.S. average.'>
              If you're completely stuck trying to come up with your monthly
              housing cost in retirement, you can use the national average as a
              starting point. The U.S. Department of Labor releases statistics
              on consumer spending on a regular basis.
              <ALink href='https://www.bls.gov/news.release/cesmy.nr0.htm'>
                The most recent news release of this information
              </ALink>
              (released April 19, 2017) indicates that the average household
              spent $18,495 on housing between July 2015 and June 2016. This
              comes out to just about $1,541 per month.
            </PopOver>{' '}
            Go ahead and enter your estimated monthly housing cost now.
          </HelpFilter>{' '}
        </CalcParagraph>
        <Form style={formConstants.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={formConstants.housingLabel}
            dollarForm
          />
        </Form>

        <HelpFilter showLevel={[1, 2]}>
          <CalcParagraph>
            Do the same thing for utilities, food, transportation and insurance.
            <HelpFilter showLevel={[1]}>
              Again, do not worry about inflation - enter the number in "today's
              dollars". If you're not exactly sure what you'll need in
              retirement for any item, consider what you spend now relative to
              what you'd like in retirement and if you are stuck,{' '}
              <PopOver linkText='check out the U.S. averages'>
                The U.S. Department of Labor releases statistics on consumer
                spending on a regular basis. We have to use the latest "annual"
                numbers to get the detail we need.
                <ALink href='https://www.bls.gov/news.release/cesan.nr0.htm'>
                  The most recent annual release of this information for 2015
                </ALink>
                (published August, 2016) indicates that the average household
                spent (per month):
                <ul style={sectionConstants.listStyle}>
                  <li>Utilities: $324</li>
                  <li>
                    Food: $585 (includes at home and dining out). Note that this
                    is per person.
                  </li>
                  <li>
                    Transportation: $791. Note that this number is an average
                    and includes vehicle purchases. So it may be well above what
                    a retiree would expect to spend per month
                  </li>
                  <li>
                    Insurance: $777 (includes health and "personal"). Note that
                    this number may be well above what a retiree would expect to
                    spend particularly if planning to rely on Medicaid.
                  </li>
                </ul>
              </PopOver>{' '}
              for inspiration or just copy directly for now.
            </HelpFilter>{' '}
          </CalcParagraph>
        </HelpFilter>
        <Form style={formConstants.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={formConstants.utilitiesLabel}
            dollarForm
          />
          <FormattedConnectedInput label={formConstants.foodLabel} dollarForm />
          <FormattedConnectedInput
            label={formConstants.transportationLabel}
            dollarForm
          />
          <FormattedConnectedInput
            label={formConstants.insuranceLabel}
            dollarForm
          />
        </Form>
        <FormNumberTotal
          formKeys={formConstants.income1FormKeys}
          sectionNumber={1}
        />
        <NavBar>
          <NavBtn nextSection={0} back />
          <NavBtn nextSection={2} />
        </NavBar>
      </CalcSection>
    )
  }
}

CalcSection.propTypes = {
  title: PropTypes.string.isRequired
}



// WEBPACK FOOTER //
// ./src/components/sections/Section1MonthlyCosts.js